import { useForm } from '@tanstack/react-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { z } from 'zod';

import { Button, CardContent } from '@cold-ambulance/shadcn';
import {
  FieldMessage,
  FormMessage,
  UploadField,
  errorToFormIssue,
  fileField,
  uploadFile,
  useAction,
} from '@cold-ambulance/ui';

const documentsSchema = z.object({
  commercialRegistrationFile: fileField,
  taxCertificate: fileField,
});

export default function AdditionalDocuments() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const updateCustomer = useAction('PATCH /customer/me', {
    onSuccess: () => {
      toast.success(t('shared.actions.profile.additionalDocsSubmitted'));
      navigate('/');
    },
  });

  const form = useForm({
    defaultValues: {
      commercialRegistrationFile: null as File | null,
      taxCertificate: null as File | null,
    },
    validators: {
      onSubmit: documentsSchema,
      onSubmitAsync: async ({ value }) => {
        try {
          const [commercialRegistrationId, taxCertificateId] =
            await Promise.all([
              uploadFile(value.commercialRegistrationFile!),
              uploadFile(value.taxCertificate!),
            ]);

          await updateCustomer.mutateAsync({
            commercialRegistrationFile: commercialRegistrationId,
            taxCertificate: taxCertificateId,
          });

          return void 0;
        } catch (error) {
          return errorToFormIssue(error);
        }
      },
    },
  });

  return (
    <div className="page-container">
      <div className="mx-auto max-w-md">
        <h1 className="mb-8 text-3xl font-bold">{t('requests.additionalDocuments')}</h1>
        <CardContent className="px-0">
          <form
            className="mt-4 mb-4 space-y-6"
            onSubmit={async (event) => {
              event.preventDefault();
              event.stopPropagation();
              await form.handleSubmit();
            }}
          >
            <form.Field
              name="commercialRegistrationFile"
              children={(field) => (
                <div className="space-y-2">
                  <UploadField
                    label={t('documents.commercialRegistration')}
                    name={field.name}
                    value={field.state.value}
                    onChange={(file) => {
                      field.handleChange(file);
                      field.handleBlur();
                    }}
                  />
                  <FieldMessage errors={field.state.meta.errors} />
                </div>
              )}
            />

            <form.Field
              name="taxCertificate"
              children={(field) => (
                <div className="space-y-2">
                  <UploadField
                    label={t('documents.taxCertificate')}
                    name={field.name}
                    value={field.state.value}
                    onChange={(file) => {
                      field.handleChange(file);
                      field.handleBlur();
                    }}
                  />
                  <FieldMessage errors={field.state.meta.errors} />
                </div>
              )}
            />

            <form.Subscribe
              selector={(state) => [state.canSubmit, state.isSubmitting]}
              children={([canSubmit, isSubmitting]) => (
                <Button
                  type="submit"
                  disabled={!canSubmit || isSubmitting}
                  className="mt-1 w-full cursor-pointer"
                >
                  {isSubmitting
                    ? t('requests.submitting')
                    : t('requests.submitAdditionalDocs')}
                </Button>
              )}
            />
            <form.Subscribe
              selector={(state) => state.errors}
              children={(errors) => <FormMessage errors={errors} />}
            />
          </form>
        </CardContent>
      </div>
    </div>
  );
}
